import NetInfo from '@/adapters/netinfo';
import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { apolloPerformanceLink } from './apollo-performance-link';
import { apolloSentryTraceLink } from './apollo-sentry-trace-link';
import {
  apolloTokenRefreshLink,
  JWT_EXPIRED_STRING,
} from './apollo-token-refresh-link';
import { apolloTrueTimeLink } from './apollo-true-time-link';
import { AuthService } from '@domain/auth/blocs/services/auth-service';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { buildSentryErrorLink } from 'apollo-sentry-helper';
import { captureException, ErrorReporter } from './sentry';
import { HeaderBuilder } from '@platform/http/blocs/services/header-builder';
import { isOfflineAuthTokenValid } from './offline-mode';
import { OfflineAuthAdapter } from '@/adapters/offline-mode';
import { OfflineModeSimulator } from './offline-mode/offline-mode-simulator';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { SentryLink } from 'apollo-link-sentry';
import { setContext } from '@apollo/client/link/context';
import { shishitoConfig } from './environment';

export const JWS_INVALID_SIGNATURE_STRING = 'JWSInvalidSignature';
export const NO_CURRENT_USER_ERROR = 'No current user';
export const NOT_AUTHORIZED_EXCEPTION_CODE = 'NotAuthorizedException';
export const NOT_FOUND_CODE = 'NOT_FOUND';
const GRAPHQL_ERROR_CODE_TAG = 'graphQLErrorCode';

type CallbackType = () => void;

export const generateSentryReporter = (onJWTCallback?: CallbackType) => {
  return buildSentryErrorLink({
    filter: (err) => {
      const { networkError } = err;
      if (networkError) {
        // Prevent NotAuthorizedException errors from being reported to Sentry as
        // it's usually a problem with the refresh token (expired, revoked, etc.)
        // @ts-ignore code exists
        if (networkError.code === NOT_AUTHORIZED_EXCEPTION_CODE) {
          return false;
        }

        if (
          // @ts-ignore networkError may be a string
          // https://github.com/jungsoft/apollo-sentry-helper/blob/master/package/src/links/sentryErrorLink/index.ts#L45
          networkError === NO_CURRENT_USER_ERROR ||
          networkError.message === NO_CURRENT_USER_ERROR
        ) {
          return false;
        }

        if (networkError.message?.includes(JWT_EXPIRED_STRING)) {
          onJWTCallback?.();
          return false;
        }
      }

      const graphQLErrors = err.graphQLErrors || [];

      // Set GraphQL error code tag with the first one present in the graphQLErrors array
      const errorCode = graphQLErrors.find(
        (gqlError) => gqlError.extensions?.code,
      );

      ErrorReporter.setTag(
        GRAPHQL_ERROR_CODE_TAG,
        errorCode?.extensions?.code as string | undefined,
      );

      // GraphQL operations can define an array of ignoreGraphQLErrorCodes in the
      // operation context to prevent those errors from being sent to Sentry
      const ignoreGraphQLErrorCodes =
        err.operation.getContext().ignoreGraphQLErrorCodes;
      if (Array.isArray(ignoreGraphQLErrorCodes)) {
        const hasIgnorableError = graphQLErrors.some((gqlError) => {
          return ignoreGraphQLErrorCodes.includes(gqlError.extensions?.code);
        });

        if (hasIgnorableError) {
          return false;
        }
      }

      const hasJWTError = graphQLErrors.some((gqlError) => {
        return gqlError?.message.includes(JWT_EXPIRED_STRING);
      });

      if (hasJWTError) {
        onJWTCallback?.();
      }

      return !hasJWTError;
    },
    // Cast to fix a type mismatch in apollo-sentry-helper
  }) as unknown as ApolloLink;
};

const defaultSentryReporter = generateSentryReporter();

export const sharedCache = new InMemoryCache({
  dataIdFromObject: ({ __typename, uuid }) => {
    // Do not cache queried objects that do not return an uuid
    return __typename && uuid ? `${__typename}:${uuid}` : undefined;
  },
  // Disable Apollo warning about lost cache data for array fields since we should always take the incoming value
  typePolicies: {
    chats: {
      fields: {
        orders: { merge: false },
      },
    },
    orders: {
      fields: {
        orders_items: { merge: false },
      },
    },
    Query: {
      fields: {
        getBusinessDetails: { merge: true },
        getCriticalItems: { merge: false },
        getOrderGuideItems: { merge: false },
        getPriceQuotes: { merge: false },
        getSalesPortalAccountListInfoPaginated: {
          keyArgs: [
            'business_organization_id',
            'filters',
            'page_size',
            'user_timezone',
          ],
          merge(existing, incoming, { args }) {
            // First page (no cursor) replaces the list; fetchMore (with a
            // cursor) appends the next page to the accumulated accounts.
            const previousAccounts =
              args?.cursor && existing ? existing.accounts : [];
            return {
              ...incoming,
              accounts: [...previousAccounts, ...incoming.accounts],
            };
          },
        },
        getSalesPortalAccountsPaginated: { merge: false },
        getUnpaidInvoiceList: {
          keyArgs: [
            'overdue_bucket',
            'record_type',
            'search_input',
            'sort_by',
            'sort_direction',
          ],
          merge(existing, incoming, { args }) {
            // First page (offset 0) replaces the list; fetchMore (with an
            // offset) appends the next page to the accumulated invoices.
            const { invoices: incomingInvoices, ...rest } = incoming;
            const previousInvoices =
              args?.offset && existing ? existing.invoices : [];
            return {
              ...rest,
              invoices: [...previousInvoices, ...incomingInvoices],
            };
          },
        },
        order_guide_restaurant_collection: { merge: false },
        orders: { merge: false },
      },
    },
  },
});

const retryLink = new RetryLink({
  attempts: {
    max: 3,
  },
  delay: {
    initial: 1000,
    jitter: true,
    max: Infinity,
  },
});

export const apolloClient = new ApolloClient({
  cache: sharedCache,
  connectToDevTools: process.env.NODE_ENV !== 'test',
  link: ApolloLink.from([
    // SentryLink adds graphql data to a sentry error and groups them together
    new SentryLink(),
    // Breadcrumb both transport failures and application-level GraphQL
    // errors so later error reports include the failing operation as context.
    onError(({ graphQLErrors, networkError, operation }) => {
      if (networkError) {
        // Some errors like `No current user` are reported as a string
        const isNetworkErrorObject = typeof networkError === 'object';
        ErrorReporter.addBreadcrumb({
          category: 'graphql.network',
          data: {
            operationName: operation.operationName,
            statusCode:
              isNetworkErrorObject && 'statusCode' in networkError
                ? networkError.statusCode
                : undefined,
          },
          level: 'warning',
          message: `${operation.operationName ?? 'GraphQL'} → ${isNetworkErrorObject ? networkError.message : networkError}`,
        });
      }
      if (graphQLErrors?.length) {
        ErrorReporter.addBreadcrumb({
          category: 'graphql.error',
          data: {
            errorCodes: graphQLErrors
              .map((e) => e.extensions?.code)
              .filter(Boolean),
            errorCount: graphQLErrors.length,
            operationName: operation.operationName,
          },
          level: 'warning',
          message: `${operation.operationName ?? 'GraphQL'} → ${graphQLErrors[0].message}`,
        });
      }
    }),
    onError(() => {
      // Reset GraphQL error code tag on the way back up the link chain
      ErrorReporter.setTag(GRAPHQL_ERROR_CODE_TAG, undefined);
    }),
    // Sign out if the JWS signature is invalid to prevent an infinite loading
    // state, which can occur when there's an existing access token but we've
    // failed over to a different Cognito user pool
    onError(({ graphQLErrors, networkError }) => {
      const hasInvalidSignature =
        graphQLErrors?.some((error) =>
          error.message?.includes(JWS_INVALID_SIGNATURE_STRING),
        ) || networkError?.message?.includes(JWS_INVALID_SIGNATURE_STRING);

      if (hasInvalidSignature) {
        void AuthService.signOut();
      }
    }),
    apolloTokenRefreshLink,
    ApolloLink.split(
      // In dev mode, add a link that will simulate offline mode by raising an error on every request
      () => __DEV__,
      (operation, forward) => {
        const isOfflineModeSimulated =
          OfflineModeSimulator.getInstance().checkEnabled();
        if (isOfflineModeSimulated) {
          return new Observable((observer) => {
            observer.error(new Error('No internet connection (simulated)'));
          });
        }

        return forward(operation);
      },
    ),
    ApolloLink.split(
      () => __DEV__,
      // log errors in dev, report to sentry otherwise
      onError(({ graphQLErrors, networkError, operation, response }) => {
        const context = {
          extra: {
            operationName: operation.operationName,
            response,
          },
        };

        if (graphQLErrors) {
          const ignoreList =
            operation.getContext().ignoreGraphQLErrorCodes || [];
          graphQLErrors
            .filter((error) => !ignoreList.includes(error.extensions?.code))
            .forEach((error) => {
              captureException(error, context);
            });
        }

        if (
          networkError &&
          // @ts-ignore code exists for NotAuthorizedException errors
          networkError.code !== NOT_AUTHORIZED_EXCEPTION_CODE
        ) {
          captureException(networkError, context);
        }
      }),
      // sentry reporter reports the error to sentry after it has gone through
      // the error link
      defaultSentryReporter,
    ),
    ApolloLink.split(
      (operation) => operation.getContext().retryEnabled,
      retryLink,
      new ApolloLink((operation, forward) => forward(operation)),
    ),
    ApolloLink.from([
      ApolloLink.split(
        (operation) => Boolean(operation.getContext().anonymous),
        setContext(async () => ({
          headers: await HeaderBuilder.create().build(),
        })),
        setContext(async (_request, previousContext) => {
          try {
            const builder = HeaderBuilder.create().withAuth().withContentType();

            if (previousContext.offlineSync) {
              builder.withHeader('X-Pepper-Offline-Sync', 'true');
            }

            return {
              headers: await builder.build(),
            };
          } catch (e) {
            // Allow the request to go through if the user is not
            // logged in and its allowed by the anonymous role
            if (previousContext.anonymous) {
              return {};
            } else {
              // If the user is offline and has a valid offline mode auth token,
              // allow the request to go through (even though it will fail downstream)
              const networkState = await NetInfo.fetch();
              const offlineAuthToken = await new OfflineAuthAdapter().fetch();
              const offlineAuthTokenValid = offlineAuthToken
                ? isOfflineAuthTokenValid(offlineAuthToken)
                : false;
              if (
                (!networkState.isConnected ||
                  !networkState.isInternetReachable) &&
                offlineAuthTokenValid
              ) {
                return {};
              }

              // Otherwise let the exception through
              throw e;
            }
          }
        }),
      ),
      apolloPerformanceLink,
      apolloSentryTraceLink,
      apolloTrueTimeLink,
      ApolloLink.split(
        (operation) => Boolean(operation.getContext().noBatch),
        ApolloLink.split(
          (operation) => Boolean(operation.getContext().relay),
          createHttpLink({ uri: shishitoConfig.graphQLRelayURL }),
          createHttpLink({ uri: shishitoConfig.graphQLEndpointURL }),
        ),
        ApolloLink.split(
          (operation) => Boolean(operation.getContext().relay),
          new BatchHttpLink({ uri: shishitoConfig.graphQLRelayURL }),
          new BatchHttpLink({ uri: shishitoConfig.graphQLEndpointURL }),
        ),
      ),
    ]),
  ]),
});

export const getCodeFromGQLError = (error: unknown) => {
  if (error instanceof ApolloError) {
    const code = error.graphQLErrors[0]?.extensions?.code;
    if (typeof code === 'string') {
      return code;
    }
  }

  return null;
};

export const getMessageFromGQLError = (error: ApolloError) => {
  return error.graphQLErrors[0]?.message ?? null;
};
